import { z } from 'zod';
import { SuggestedMessagingArea, SuggestedMessagingAreaSchema } from './suggested-messaging';
import { LiquidProductVariantSchema } from '../shopify/liquid/variant';
import { ShopifyIDSchema } from '../shopify';

// ---- Enums ----

export const UVInsertFaceElementTypeSchema = z.enum(['INSERT', 'GREETINGS_CARD', 'LANDSCAPE_INSERT']);
export type UVInsertFaceElementType = z.infer<typeof UVInsertFaceElementTypeSchema>;

export const UVInsertFaceElementLocationSchema = z.enum(['FRONT', 'INSIDE_LEFT', 'INSIDE_RIGHT', 'BACK']);
export type UVInsertFaceElementLocation = z.infer<typeof UVInsertFaceElementLocationSchema>;

// ---- Shared sub-schemas ----

const SizeDimensionSchema = z.union([z.string(), z.number()]).transform((v) => String(v));

const SizeSchema = z.object({
  minHeight: SizeDimensionSchema.optional(),
  maxHeight: SizeDimensionSchema.optional(),
  height: SizeDimensionSchema.optional(),
  minWidth: SizeDimensionSchema.optional(),
  maxWidth: SizeDimensionSchema.optional(),
  width: SizeDimensionSchema.optional()
});

const PositioningSchema = z.object({
  top: z.string().optional(),
  right: z.string().optional(),
  bottom: z.string().optional(),
  left: z.string().optional()
});

/** CSS `font-size`: bare numbers from Liquid become `${n}px` in the visualiser; strings pass through (e.g. `1rem`, `14px`). */
const UVInsertTextFontSizeSchema = z.union([z.number(), z.string()]);

// ---- Face element config schemas (`z.union`: TEXT static keyed vs display-only vs editable; discriminatedUnion cannot wrap refinements). ----

const UVInsertFaceElementTextStaticKeyedConfigSchema = z.object({
  type: z.literal('TEXT'),
  editable: z.literal(false),
  lineItemPropertyKey: z.string().min(1),
  /** Initial copy when `textByKey` has no value yet. */
  value: z.string().optional(),
  /** When true, suggested-message picks on this tab apply to this key (see shared view `getSuggestionTargetTextPropertyKeyForRegionTab`). */
  suggestionTarget: z.boolean().optional(),
  /**
   * When true, the host populates this key from other slots (e.g. a derived summary) rather
   * than the customer typing into it. The host must not render an input for a derived element
   * and is responsible for writing `textByKey[lineItemPropertyKey]` itself; it is still emitted
   * as a line item property when non-empty. Only valid on static keyed TEXT (`editable: false`
   * with a `lineItemPropertyKey`) — display-only TEXT, editable TEXT, and IMAGE_UPLOAD must not
   * accept it.
   */
  derived: z.boolean().optional(),
  font: z.string(),
  fontWeight: z.string().optional(),
  fontSize: UVInsertTextFontSizeSchema.optional(),
  minFontSize: z.number().positive().optional(),
  lineHeight: z.number().optional(),
  color: z.string().optional(),
  maxLength: z.number().int().positive(),
  maxLines: z.number().int().positive(),
  size: SizeSchema.default({}),
  positioning: PositioningSchema.default({}),
  style: z.record(z.string(), z.union([z.string(), z.number()])).default({})
});

const UVInsertFaceElementTextStaticDisplayConfigSchema = z
  .object({
    type: z.literal('TEXT'),
    editable: z.literal(false),
    value: z.string(),
    lineItemPropertyKey: z.string().optional().default(''),
    /** Display-only TEXT has no line item key to populate, so `derived` never applies here. */
    derived: z.boolean().optional(),
    font: z.string(),
    fontWeight: z.string().optional(),
    fontSize: UVInsertTextFontSizeSchema.optional(),
    minFontSize: z.number().positive().optional(),
    lineHeight: z.number().optional(),
    color: z.string().optional(),
    maxLength: z.number().int().positive(),
    maxLines: z.number().int().positive(),
    size: SizeSchema.default({}),
    positioning: PositioningSchema.default({}),
    style: z.record(z.string(), z.union([z.string(), z.number()])).default({})
  })
  .refine((d) => !(d.lineItemPropertyKey ?? '').trim(), {
    path: ['lineItemPropertyKey'],
    message: 'display-only static TEXT must omit lineItemPropertyKey or use an empty string'
  })
  .refine((d) => d.derived !== true, {
    path: ['derived'],
    message: 'display-only static TEXT must not use derived (no lineItemPropertyKey to populate)'
  });

const UVInsertFaceElementTextEditableConfigSchema = z
  .object({
    type: z.literal('TEXT'),
    lineItemPropertyKey: z.string().min(1),
    /** Omitted or `true` in Liquid; must not be `false` (that shape uses a static TEXT schema). */
    editable: z.boolean().optional(),
    value: z.string().optional(),
    suggestionTarget: z.boolean().optional(),
    /** When set, the step renders a labelled field for this element below the face, bound to the same text. */
    inputLabel: z.string().optional(),
    /** Editable TEXT is customer-typed by definition, so `derived` never applies here. */
    derived: z.boolean().optional(),
    font: z.string(),
    fontWeight: z.string().optional(),
    fontSize: UVInsertTextFontSizeSchema.optional(),
    minFontSize: z.number().positive().optional(),
    lineHeight: z.number().optional(),
    color: z.string().optional(),
    maxLength: z.number().int().positive(),
    maxLines: z.number().int().positive(),
    /**
     * Print wrap width in characters. When set, the message is also capped so it wraps to no more
     * than `maxLines` lines at this width (see `wrapUvInsertTextByChars`).
     */
    maxCharsPerLine: z.number().int().positive().optional(),
    size: SizeSchema.default({}),
    positioning: PositioningSchema.default({}),
    style: z.record(z.string(), z.union([z.string(), z.number()])).default({})
  })
  .refine((d) => d.editable !== false, {
    path: ['editable'],
    message: 'editable: false must use static TEXT config (keyed label or display-only)'
  })
  .refine((d) => d.derived !== true, {
    path: ['derived'],
    message: 'editable TEXT must not use derived (only static keyed TEXT may)'
  });

const UVInsertFaceElementImageConfigSchema = z
  .object({
    type: z.literal('IMAGE_UPLOAD'),
    lineItemPropertyKey: z.string().min(1),
    /** When set, the step renders a labelled "Choose File" field for this element below the face. */
    inputLabel: z.string().optional(),
    /** When true, an opted-in insert is not complete until this element has an image. */
    required: z.boolean().optional(),
    /** IMAGE_UPLOAD has no textByKey slot, so `derived` never applies here. */
    derived: z.boolean().optional(),
    size: SizeSchema.default({}),
    positioning: PositioningSchema.default({}),
    style: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
    backgroundColor: z.string().optional(),
    borderRadius: z.string().optional()
  })
  .refine((d) => d.derived !== true, {
    path: ['derived'],
    message: 'IMAGE_UPLOAD must not use derived'
  });

/**
 * Star bullet defaults, applied where the bullet is read rather than in the schema so a config that
 * sets none of them parses to exactly the object it always did (`{ glyph: '★' }`).
 */
export const UV_INSERT_TEXT_LIST_BULLET_DEFAULTS = {
  shape: 'glyph',
  /** Star width in px. */
  size: 8,
  fillColor: '#ffffff',
  outlineColor: '#231f20',
  /** Star outline width in px. */
  outlineWidth: 0.3
} as const;

/**
 * A row-list element that renders N rows (e.g. "★ + month year / moment") from `textByKey`.
 * The element itself is render-only: it never owns a `textByKey` entry of its own, and the host
 * merges the per-row keys in. `textByKey` stays `Record<string,string>`.
 *
 * The keys divide by who writes them:
 * - `${monthKeyPrefix}${i}` — **host-derived** (e.g. from the chosen birthstones). This is what
 *   `derived: true` means for a `TEXT_LIST`: the host populates every key — month, year and moment
 *   — from the stones step's paired slots and locks them all against edits on the card.
 * - `${yearKeyPrefix}${i}` and `${momentKeyPrefix}${i}` — typed by the customer on the **stones
 *   step** (paired `ALPHANUMERIC` slots under each stone), then derived onto the card exactly as the
 *   month is. Nothing on the card face or beside it edits them.
 *
 * Every field added since the original shape is optional or defaulted, so a config that opts into
 * none of them parses exactly as before. This is a plain `z.object` (not `.strict()`), so unknown
 * keys are stripped rather than rejected — Liquid may ship a key ahead of the schema.
 */
const UVInsertFaceElementTextListConfigSchema = z.object({
  type: z.literal('TEXT_LIST'),
  /** Every key — month, year and moment — is host-derived from the stones step and locked on the card. */
  derived: z.literal(true),
  /** Maximum rows rendered; keys are `${monthKeyPrefix}${i}` / `${momentKeyPrefix}${i}` for i = 1..rows. */
  rows: z.number().int().positive(),
  monthKeyPrefix: z.string().min(1),
  momentKeyPrefix: z.string().min(1),
  /**
   * When set, `${yearKeyPrefix}${i}` is a four-digit year printed after the month, derived from the
   * row's paired year slot on the stones step (see {@link UV_INSERT_TEXT_LIST_YEAR_PATTERN} — a
   * value that is not exactly four digits is treated as absent). Absent means no year on the card.
   */
  yearKeyPrefix: z.string().min(1).optional(),
  /** Maximum characters accepted in a row's moment input. */
  momentMaxLength: z.number().int().positive().default(40),
  /** Horizontal alignment of the row text; `'right'` anchors rows at the element's right edge. */
  align: z.enum(['left', 'right']).default('left'),
  font: z.string(),
  fontWeight: z.string().optional(),
  fontSize: UVInsertTextFontSizeSchema.optional(),
  lineHeight: z.number().optional(),
  color: z.string().optional(),
  /** Vertical gap between rows in px. Ignored when `rowPitch` is set. */
  rowGap: z.number().nonnegative().default(6),
  /** When set, row i (0-based) starts `i × rowPitch` px below the content top and `rowGap` is ignored. */
  rowPitch: z.number().positive().optional(),
  /**
   * Px from the headline line's centre to the first moment line's centre. Absent stacks the moment
   * directly under the headline (the original layout).
   */
  momentOffset: z.number().positive().optional(),
  /**
   * Leading marker per row. An empty `glyph` means "no bullet". Colour resolution per row:
   * `colorByMonth[<derived month label>]` → `bullet.color` → the element's `color`; in `'star'`
   * mode the last step is `bullet.fillColor` instead, since the star has its own fill.
   */
  bullet: z
    .object({
      /** Absent means `'glyph'`. */
      shape: z.enum(['glyph', 'star']).optional(),
      glyph: z.string().default('★'),
      color: z.string().optional(),
      colorByMonth: z.record(z.string(), z.string()).optional(),
      /** Star width in px (`'star'` shape only); absent means {@link UV_INSERT_TEXT_LIST_BULLET_DEFAULTS}`.size`. */
      size: z.number().positive().optional(),
      /** Px between the bullet and the text; glyph mode keeps `0.35em` when absent. */
      gap: z.number().nonnegative().optional(),
      /** Star fill (`'star'` shape only); absent means the default fill. */
      fillColor: z.string().optional(),
      /** Star outline colour (`'star'` shape only); absent means the default outline. */
      outlineColor: z.string().optional(),
      /** Star outline width in px (`'star'` shape only); absent means the default width. */
      outlineWidth: z.number().nonnegative().optional()
    })
    .default({}),
  /**
   * Optional vertical rule drawn down the anchor side of the list, with a filled dot at each end
   * (the Story of Us timeline). It is presentation only — no key, no line item property — and it
   * **flexes to the rows actually drawn**, which is the whole point of drawing it here rather than
   * baking it into the card artwork: a 5-row card, a 7-row card and a 7-row card with three stones
   * left blank all need a different line.
   *
   * Absent means no rule at all: an element that does not opt in renders exactly as it did before
   * this field existed. Geometry lives in {@link getUvInsertTextListRuleMetrics} so the DOM list and
   * the canvas cart preview cannot drift.
   */
  rule: z
    .object({
      /** Line thickness in px. */
      width: z.number().positive().default(1),
      /** Rule colour; falls back to the element's `color` when omitted. */
      color: z.string().optional(),
      /** Dot radius in px. `0` draws the line with no end dots. */
      dotRadius: z.number().nonnegative().default(2),
      /** Px between the text edge and the rule. */
      gap: z.number().nonnegative().default(4.5),
      /** Px the rule runs past the first and last row. */
      overhang: z.number().nonnegative().default(5)
    })
    .optional(),
  size: SizeSchema.default({}),
  positioning: PositioningSchema.default({}),
  style: z.record(z.string(), z.union([z.string(), z.number()])).default({})
});

export type UVInsertFaceElementTextListConfig = z.infer<typeof UVInsertFaceElementTextListConfigSchema>;

/** The parsed, defaults-applied `rule` object on a `TEXT_LIST` element. */
export type UVInsertTextListRuleConfig = NonNullable<UVInsertFaceElementTextListConfig['rule']>;

const UVInsertFaceElementConfigSchema = z.union([
  UVInsertFaceElementTextStaticKeyedConfigSchema,
  UVInsertFaceElementTextStaticDisplayConfigSchema,
  UVInsertFaceElementTextEditableConfigSchema,
  UVInsertFaceElementImageConfigSchema,
  UVInsertFaceElementTextListConfigSchema
]);

/** Parsed from Liquid / DTOs (no session value/setValue). */
export type UVInsertFaceElementConfigData = z.infer<typeof UVInsertFaceElementConfigSchema>;

/**
 * TEXT elements stored in `textByKey` and emitted as line item properties when non-empty.
 * Includes editable fields and read-only keyed labels (derived or not); excludes display-only
 * static TEXT (no LIP key) and `TEXT_LIST` (its keys are host-populated; the host merges them
 * into `textByKey` itself, so a list is never itself a `textByKey` entry).
 */
export function isUvInsertUserConfigurableText(
  el: UVInsertFaceElementConfigData
): el is
  | z.infer<typeof UVInsertFaceElementTextEditableConfigSchema>
  | z.infer<typeof UVInsertFaceElementTextStaticKeyedConfigSchema> {
  return el.type === 'TEXT' && Boolean(el.lineItemPropertyKey?.trim());
}

/** True for the `TEXT_LIST` row-list element. */
export function isUvInsertTextListElement(
  el: UVInsertFaceElementConfigData
): el is UVInsertFaceElementTextListConfig {
  return el.type === 'TEXT_LIST';
}

/**
 * True for any element carrying host-populated keys: `TEXT_LIST` (every key it renders — month,
 * year and moment — is derived by the host from the stones step's paired slots), and static keyed
 * TEXT marked `derived: true`. Callers that lock keys against customer edits lock all of a
 * `TEXT_LIST`'s keys; the customer types the year and moment on the stones step, never on the card.
 */
export function isUvInsertDerivedElement(el: UVInsertFaceElementConfigData): boolean {
  if (isUvInsertTextListElement(el)) return true;
  return el.type === 'TEXT' && 'derived' in el && el.derived === true;
}

/** One row's `textByKey` keys. `yearKey` is `null` when the element declares no `yearKeyPrefix`. */
export type UvInsertTextListRowKeys = {
  index: number;
  monthKey: string;
  yearKey: string | null;
  momentKey: string;
};

/**
 * The 1-based `{ index, monthKey, yearKey, momentKey }` rows a `TEXT_LIST` element renders, keyed
 * `${monthKeyPrefix}${i}` / `${yearKeyPrefix}${i}` / `${momentKeyPrefix}${i}` for `i = 1..rows`.
 * `yearKey` is `null` for a config without `yearKeyPrefix` (no year field, no year on the card).
 */
export function getUvInsertTextListKeys(options: {
  element: UVInsertFaceElementTextListConfig;
}): UvInsertTextListRowKeys[] {
  const { element } = options;
  const keys: UvInsertTextListRowKeys[] = [];
  for (let index = 1; index <= element.rows; index++) {
    keys.push({
      index,
      monthKey: `${element.monthKeyPrefix}${index}`,
      yearKey: element.yearKeyPrefix ? `${element.yearKeyPrefix}${index}` : null,
      momentKey: `${element.momentKeyPrefix}${index}`
    });
  }
  return keys;
}

/** A `TEXT_LIST` year is exactly four digits; anything else is treated as "no year". */
export const UV_INSERT_TEXT_LIST_YEAR_PATTERN = /^\d{4}$/;

/** True only when `value` is exactly four digits (see {@link UV_INSERT_TEXT_LIST_YEAR_PATTERN}). */
export function isUvInsertTextListYear(value: string | null | undefined): boolean {
  if (typeof value !== 'string') return false;
  return UV_INSERT_TEXT_LIST_YEAR_PATTERN.test(value);
}

// ---- Hydrated UI types (value/setValue layered on top in the theme) ----

/**
 * Maps each config type to its strongly-typed value and setValue pair.
 * Add new entries here to extend the discriminated union automatically.
 */
type UVInsertFaceElementConfigMap = {
  TEXT: {
    lineItemPropertyKey: string;
    value: string;
    setValue: (value: string) => void;
    font: string;
    fontWeight?: string;
    fontSize?: number | string;
    minFontSize?: number;
    lineHeight?: number;
    color?: string;
    maxLength: number;
    maxLines: number;
    maxCharsPerLine?: number;
    inputLabel?: string;
  };
  IMAGE_UPLOAD: {
    lineItemPropertyKey: string;
    value: File;
    setValue: (value: File) => void;
    inputLabel?: string;
    required?: boolean;
    backgroundColor?: string;
    borderRadius?: string;
  };
  /**
   * Row-list element: no input on the face, so no `setValue`. Value is the `textByKey` slice for
   * its keys — months, years and moments, all host-derived from the stones step's paired slots.
   */
  TEXT_LIST: {
    value: Record<string, string>;
    font: string;
    fontWeight?: string;
    fontSize?: number | string;
    lineHeight?: number;
    color?: string;
    rows: number;
    monthKeyPrefix: string;
    momentKeyPrefix: string;
    yearKeyPrefix?: string;
    momentMaxLength: number;
    align: 'left' | 'right';
    rowGap: number;
    rowPitch?: number;
    momentOffset?: number;
    bullet: {
      shape?: 'glyph' | 'star';
      glyph: string;
      color?: string;
      colorByMonth?: Record<string, string>;
      size?: number;
      gap?: number;
      fillColor?: string;
      outlineColor?: string;
      outlineWidth?: number;
    };
  };
};

/**
 * Shared properties across all UVInsertFaceElementConfig variants, independent of the config
 * type. `lineItemPropertyKey` lives on the per-type map entries instead — `TEXT_LIST` has no
 * line item property of its own (its `textByKey` keys come from {@link getUvInsertTextListKeys}).
 */
type UVInsertFaceElementConfigBase = {
  size: {
    minHeight?: string;
    maxHeight?: string;
    height?: string;
    minWidth?: string;
    maxWidth?: string;
    width?: string;
  };
  positioning: {
    top?: string;
    right?: string;
    bottom?: string;
    left?: string;
  };
  style: Record<string, string | number>;
};

/**
 * A discriminated union of all possible face element config variants.
 * Each member is the base config merged with a specific type key
 * and its corresponding value/setValue types from UVInsertFaceElementConfigMap.
 *
 * Distributing over keyof UVInsertFaceElementConfigMap at the end
 * produces a union rather than a single merged type, enabling
 * TypeScript to narrow value and setValue when type is known.
 */
export type UVInsertFaceElementConfig = {
  [K in keyof UVInsertFaceElementConfigMap]: UVInsertFaceElementConfigBase & {
    type: K;
  } & UVInsertFaceElementConfigMap[K];
}[keyof UVInsertFaceElementConfigMap];

/** Convenience type for text-based face element configs */
export type UVInsertFaceElementTextConfig = Extract<UVInsertFaceElementConfig, { type: 'TEXT' }>;

/** Convenience type for image upload face element configs */
export type UVInsertFaceElementImageConfig = Extract<UVInsertFaceElementConfig, { type: 'IMAGE_UPLOAD' }>;

// ---- Face element & config schemas ----

const UVInsertFaceElementSchema = z.object({
  type: UVInsertFaceElementTypeSchema,
  location: UVInsertFaceElementLocationSchema,
  elementConfig: z.array(UVInsertFaceElementConfigSchema).default([]),
  subtitle: z.string().optional(),
  color: z.string().optional(),
  backgroundColor: z.string().optional()
});

export interface UVInsertFaceElement {
  type: 'INSERT' | 'GREETINGS_CARD' | 'LANDSCAPE_INSERT';
  location: 'FRONT' | 'INSIDE_LEFT' | 'INSIDE_RIGHT' | 'BACK';
  /** From Liquid; use {@link UVInsertFaceElementConfig} when the theme attaches value/setValue. */
  elementConfig: UVInsertFaceElementConfigData[];
  subtitle?: string;
  color?: string;
  backgroundColor?: string;
}

/** Core configuration object for a UVInsert */
export interface UVInsertConfig {
  /** Metaobject ID */
  id: z.infer<typeof ShopifyIDSchema>;
  /** Metaobject handle */
  handle: string;
  /** Whether this UV insert config is enabled for use. */
  active: boolean;
  /** Theme variants (Liquid JSON) available for this UV insert */
  themes: z.infer<typeof LiquidProductVariantSchema>[];
  /** The name of the area this insert represents in the context of suggested messaging */
  suggestedMessagingArea?: z.infer<typeof SuggestedMessagingAreaSchema>;
  title?: string;
  /** CDN URL for the chooser thumbnail. Null when the product metafield is not set. */
  thumbnailSrc?: string | null;
  /**
   * Human-readable description for this config, declared in uv-inserts-config.liquid.
   * Shown as the subtitle on the option selection page that leads to this config.
   * Replaces the legacy window.giftWrapData price-fetching subtitle functions in pages.js.
   */
  description?: string
  /**
   * Market segment(s) this config is eligible for (e.g. `["WOMENS"]`, `["MENS"]`,
   * or both). An empty array resolves to the default segment (WOMENS) via the shared
   * market-segment rule, so existing configs need no backfilling.
   */
  marketSegment: string[]
  /** Variants added to the order with this insert (e.g. the Story of Us envelope): £0 and hidden from the cart. */
  addOnVariantIds?: number[];
  /** A list of face elements available */
  faceElements: UVInsertFaceElement[];
}

export const UVInsertConfigSchema = z.object({
  id: ShopifyIDSchema,
  handle: z.string(),
  active: z.boolean().default(true),
  themes: z.array(LiquidProductVariantSchema),
  suggestedMessagingArea: SuggestedMessagingAreaSchema.optional(),
  title: z.string().optional(),
  thumbnailSrc: z.string().nullable().optional(),
  description: z.string().optional(),
  marketSegment: z.array(z.string()).default([]),
  /** Variants added to the order with this insert (e.g. the Story of Us envelope): £0 and hidden from the cart. */
  addOnVariantIds: z.array(z.number().int().positive()).optional(),
  faceElements: z.array(UVInsertFaceElementSchema).default([]),
});


/**
 * Normalized Liquid bootstrap data after {@link readUvInsertsBootstrapPayload} reads the window.
 * `configs` and `suggestedMessaging` stay as raw JSON until DTO transforms produce domain models.
 */
export type UvInsertsBootstrapPayload = {
  configs: unknown[];
  templateIds: Record<number | string, string | null>;
  suggestedMessaging?: unknown[];
};
